// THE SEABED UNDER THE HULL: the harvesting looks that lie below the hull, so
// no Dredger mark astern can clash with them (R1120, mockup round 055
// group a).
//
// One entry per look id of `labels.toml gather_styles` that this file owns:
// `cutter-head` (card 01), `silt-bloom` (card 03) and `keel-glow` (card 05).
// `gather-styles.ts` prefers this map over the AUTO look, so a look that is
// not here yet draws the AUTO look and the map fills in one card at a time.
//
// **PENDING names every card of this file that is not built yet.** An id must
// stand in `RECIPES` or in `PENDING`, and `gather-recipes-seabed.test.ts`
// fails when one stands in neither. All three cards are built, so the table
// is empty.
//
// **A recipe reads its figures out of the row and holds no literal.** A
// PROPORTION is not a figure and stays here as a module constant, the way
// `LOOP_QUADRANT` does in `gather-recipes-matter.ts`. A card carries more
// figures than the fourteen row fields hold, so the SECONDARY figures of a
// card stand here as named constants as well, the way `FLASH_MUZZLE_FLOOR`
// does in `combat-recipes-melee.ts`. Each one names its card and says what
// the card writes.
//
// This is a leaf (`leaves.test.ts`): it imports the kit and the generated
// pack, and never an engine.

import { GATHER_LIMITS } from '../gen/presentation';
import {
  TAU, GRAIN_STRIDE, PALE_INK, clamp01, dashDuty, dashesOn, dashedRing, ease, glow,
  grain, holdEnv, lerp, lighten, line, ring, ringGlow, sinceCut, turnSec, wedge,
  type GatherCtx, type GatherRecipe,
} from './gather-recipe-kit';

// ---------------------------------------------------------------------------
// The proportions and the secondary figures
// ---------------------------------------------------------------------------

/** cutter-head, card 01: how much of one tooth pitch a tooth covers, in the
 *  gather and in the shaft. The card writes `pitch * 0.5` and `pitch * 0.4`,
 *  so a tooth is a share of the pitch and never a length of its own. */
const TOOTH_DUTY = 0.5, TOOTH_SHAFT_DUTY = 0.4;

/** cutter-head: how far back a drain step kicks the head, as a share of one
 *  pitch. The card writes `0.5 * pitch * step * step`, so the kick falls away
 *  with the square of the beat. */
const TOOTH_KICK = 0.5;

// cutter-head: the tooth is a SAW tooth, and the kit's `wedge` cell is an
// isoceles triangle. The card draws three points: the inner point at the
// tooth's own angle, the outer point above it, and the inner point one tooth
// width behind. The kit bakes ONE filled triangle (`atlas.ts gatherSpecFor`
// names it a saw tooth), so the GATHER tooth stands as the wedge whose apex
// is the outer point and whose base is the chord between the two inner
// points, which holds the lean of the saw. The SHAFT tooth is stroked, and a
// stroke takes the card's three points exactly.

/** silt-bloom, card 03: the rim a puff is born on, in world px. The card
 *  writes `cos(g) * 8` and `sin(g) * 6`: a puff rises at the rim of the
 *  field and never at its middle. */
const SILT_RIM_X = 8, SILT_RIM_Y = 6;

/** silt-bloom: how many puffs one SHAFT lot lifts. The card writes a period
 *  of 1.6 s, which is a third of the 4.8 s shaft lot. The gather's own figure
 *  is the row's `speed`, because that is the one a tuner reaches for. */
const SILT_SHAFT_PER_LOT = 3;

/** silt-bloom: where the shaft plume leaves the field and how far it wanders
 *  across the current, in world px. The card writes `4 + 17 * u` and
 *  `(h2 - 0.5) * 2`: the plume is one thin line out of the middle. */
const SILT_SHAFT_START = 4, SILT_SHAFT_SIDE = 2;

/** silt-bloom: how far down the current a puff drifts, as a share of the
 *  row's own reach. The card writes `0.75 + 0.5 * h3`, so one puff runs out
 *  ahead of the next. */
const SILT_DRIFT_MIN = 0.75, SILT_DRIFT_SPAN = 0.5;

/** silt-bloom: how much of its life a puff opens over, and how it thins away
 *  in the gather and in the shaft. The card writes `clamp(u / 0.08)` and
 *  `pow(1 - u, 1.4)`, and the shaft's thinner plume holds on longer. */
const SILT_FADE_IN = 0.08, SILT_FADE_POW = 1.4, SILT_SHAFT_FADE_POW = 0.8;

/** silt-bloom: the birth ramp of the first puffs, which follows the envelope
 *  of the cut. The card writes `clamp(born / 0.6 + 0.3)`, and the 0.6 is
 *  `ramp_in_sec`, so this is the head start the first puff keeps. */
const SILT_RAMP_BIAS = 0.3;

/** silt-bloom: which grain wears the commodity hue and how far that hue is
 *  lifted. The card writes `h3 > 0.62` and `lighten(res, 0.5)`, so about a
 *  third of the puffs carry the matter and the rest are pale water. */
const SILT_TINT_AT = 0.62, SILT_TINT_LIFT = 0.5;

/** silt-bloom: the seconds after the cut ended that the last puffs drift on
 *  for. The card writes `since > 6`. */
const SILT_TAIL_SEC = 6;

/** silt-bloom: the drain-step puff, in world px. The card writes
 *  `10 + 6 * (1 - step)`: one soft puff over the middle of the field that
 *  opens as the beat runs out. */
const SILT_STEP_R = 10, SILT_STEP_GROW = 6;

/** silt-bloom: the grain strides of one puff. They are the card's own
 *  `hash(k)`, `hash(k * 3 + 5)` and `hash(k * 7 + 11)`, which give a puff its
 *  bearing on the rim, its wander across the current and its size. */
const SILT_G2_MUL = 3, SILT_G2_ADD = 5, SILT_G3_MUL = 7, SILT_G3_ADD = 11;

/** keel-glow, card 05: the alphas of the full-hold beat, in the gather and in
 *  the shaft. The card writes `0.9 * k` and `0.7 * k`. The four breathing
 *  alphas are the row's, because they are the ones a tuner reaches for. */
const KEEL_FULL_ALPHA = 0.9, KEEL_FULL_SHAFT_ALPHA = 0.7;

/** keel-glow: how far the shaft ring's ink is lifted off the commodity hue.
 *  The card writes `lighten(res, 0.2)`, which is less than the `resHi` of the
 *  context, so a hollow ring of light never reads as a filled halo. */
const KEEL_SHAFT_LIFT = 0.2;

// ---------------------------------------------------------------------------
// The recipes
// ---------------------------------------------------------------------------

export const RECIPES: Readonly<Record<string, GatherRecipe>> = {
  /**
   * Card 01, CUTTER HEAD.
   *
   * A toothed ring turns under the hull, round the diamond, while the cut
   * runs. The head moves two teeth per lot, it kicks back on a drain step,
   * and it stops and turns cargo brown when the hold is full. The shaft shows
   * `count2` HOLLOW teeth on a dashed ring at a quarter of the pace.
   *
   * The ring has its centre on the HULL, not on the field, which is what the
   * card draws: a Dredger rests two px short of the deposit it works.
   */
  'cutter-head': {
    under(c: GatherCtx): void {
      const env = holdEnv(c);
      if (env <= 0) return;
      const r = c.row;
      const n = Math.round(c.shaft ? r.count2 : r.count);
      if (n < 1) return;
      // The head and its teeth turn cargo brown for the full-hold beat, which
      // is how the card says "the cut stopped because the hold is full".
      const full = c.full > 0;
      const tip = (c.shaft ? r.lengthPx : r.r2Px) + r.length2Px * c.step;
      if (c.shaft) {
        const dash = dashesOn(r.rPx, r.driftPx, r.driftPx);
        dashedRing(
          c, c.hx, c.hy, r.rPx, r.widthPx, dash, dashDuty(r.driftPx, r.driftPx),
          full ? c.cargo : PALE_INK, r.alpha * env,
        );
      } else {
        ring(c, c.hx, c.hy, r.rPx, r.widthPx, full ? c.cargo : PALE_INK, r.alpha * env);
      }
      const pitch = TAU / n;
      const wd = pitch * (c.shaft ? TOOTH_SHAFT_DUTY : TOOTH_DUTY);
      // `speed` is the radians the head turns over one lot, and `turnSec` is
      // the seconds the head has turned for: the integral of the cut's own
      // envelope, so the head runs up and runs down with the cut.
      const ang = (turnSec(c) * c.rate * r.speed) / GATHER_LIMITS.lotSec
        - TOOTH_KICK * pitch * c.step * c.step;
      const ink = full ? c.cargo : c.resHi;
      if (c.shaft) {
        // A hollow tooth: the radial edge leads and the sloped edge trails,
        // exactly as the card's three points stand.
        const a4 = r.alpha4 * env;
        for (let i = 0; i < n; i++) {
          const a = ang + i * pitch;
          const ca = Math.cos(a), sa = Math.sin(a);
          const cb = Math.cos(a - wd), sb = Math.sin(a - wd);
          const ax = c.hx + ca * r.rPx, ay = c.hy + sa * r.rPx;
          const bx = c.hx + ca * tip, by = c.hy + sa * tip;
          const dx = c.hx + cb * r.rPx, dy = c.hy + sb * r.rPx;
          line(c, ax, ay, bx, by, r.width2Px, ink, a4);
          line(c, bx, by, dx, dy, r.width2Px, ink, a4);
          line(c, dx, dy, ax, ay, r.width2Px, ink, a4);
        }
        return;
      }
      // A filled tooth. The wedge stands on the chord between the two inner
      // points, with its apex on the outer one.
      const half = wd / 2;
      const baseX = r.rPx * Math.cos(half), baseHalf = r.rPx * Math.sin(half);
      const len = Math.hypot(tip - baseX, baseHalf);
      const axis = Math.atan2(baseHalf, tip - baseX);
      const alpha = (r.alpha2 + r.alpha3 * c.step) * env;
      for (let i = 0; i < n; i++) {
        const a = ang + i * pitch;
        wedge(
          c, c.hx + Math.cos(a) * tip, c.hy + Math.sin(a) * tip,
          a + axis, len, baseHalf, ink, alpha,
        );
      }
    },
  },

  /**
   * Card 03, SILT BLOOM.
   *
   * Soft silt puffs rise at the rim of the field, `speed` of them a lot, and
   * drift with the current. A puff grows and thins away over its own life,
   * which is `count` periods long. When the cut stops, no new puff rises and
   * the last puffs drift off. The shaft shows a thin, slow plume in one line
   * out of the middle of the field.
   *
   * **The pool is closed.** A puff has no state of its own: its number gives
   * it its grain, and its age gives it its place, its size and its alpha. The
   * recipe walks `count + 1` numbers back from the newest and draws the ones
   * that are still alive, so the cost stands however long the cut runs.
   */
  'silt-bloom': {
    mid(c: GatherCtx): void {
      const since = sinceCut(c);
      if (since > SILT_TAIL_SEC || c.tw <= 0) return;
      const r = c.row;
      const perLot = c.shaft ? SILT_SHAFT_PER_LOT : r.speed;
      const live = Math.round(c.shaft ? r.count2 : r.count);
      if (perLot <= 0 || live < 1 || c.rate <= 0) return;
      // The period is the lot in WALL time over the puffs one lot lifts, so
      // the shaft's plume beats at a quarter of the pace with no figure of
      // its own. A puff lives for as long as `live` of them stand.
      const per = GATHER_LIMITS.lotSec / c.rate / perLot;
      const life = per * live;
      const now = c.tw + since;
      const newest = Math.floor(now / per);
      const tint = lighten(c.res, SILT_TINT_LIFT);
      const drop = c.shaft ? SILT_SHAFT_FADE_POW : SILT_FADE_POW;
      for (let j = 0; j <= live; j++) {
        const k = newest - j;
        if (k < 0) continue;
        const born = k * per;
        if (born > c.tw) continue;
        const u = (now - born) / life;
        if (u >= 1) continue;
        const g = k + c.seed * GRAIN_STRIDE;
        const h1 = grain(g), h2 = grain(g * SILT_G2_MUL + SILT_G2_ADD);
        const h3 = grain(g * SILT_G3_MUL + SILT_G3_ADD);
        // The first puffs of a cut follow the envelope: the cut ramps in over
        // `ramp_in_sec`, and so does the plume.
        const ramp = clamp01(born / GATHER_LIMITS.rampInSec + SILT_RAMP_BIAS);
        let x: number, y: number, rad: number;
        if (c.shaft) {
          const d = SILT_SHAFT_START + r.length2Px * u;
          const side = (h2 - 0.5) * SILT_SHAFT_SIDE;
          x = c.curX * d - c.curY * side;
          y = c.curY * d + c.curX * side;
          rad = lerp(r.widthPx, r.width2Px, u);
        } else {
          const bearing = h1 * TAU;
          const d = r.lengthPx * u * (SILT_DRIFT_MIN + SILT_DRIFT_SPAN * h3);
          const side = (h2 - 0.5) * r.driftPx * u;
          x = Math.cos(bearing) * SILT_RIM_X + c.curX * d - c.curY * side;
          y = Math.sin(bearing) * SILT_RIM_Y + c.curY * d + c.curX * side;
          rad = lerp(r.rPx, r.r2Px, Math.sqrt(u));
        }
        const a = r.alpha * ramp * clamp01(u / SILT_FADE_IN) * Math.pow(1 - u, drop);
        glow(c, c.fx + x, c.fy + y, rad, h3 > SILT_TINT_AT ? tint : PALE_INK, a);
      }
      // The drain step: one soft puff over the middle of the field, which
      // opens as the beat runs out.
      if (c.step > 0 && c.atRest) {
        glow(
          c, c.fx, c.fy, SILT_STEP_R + SILT_STEP_GROW * (1 - c.step),
          PALE_INK, r.alpha2 * c.step,
        );
      }
    },
  },

  /**
   * Card 05, KEEL GLOW.
   *
   * A soft halo in the commodity hue lies under the hull. It breathes one
   * time per lot: bright at the start of the lot, low at its end. A drain
   * step widens the halo for a moment. When the hold is full, the halo turns
   * cargo brown for the beat and goes out. The shaft shows a HOLLOW ring of
   * light in place of the filled halo, smaller and four times slower.
   *
   * It is the cheapest look that moves: one quad a frame.
   */
  'keel-glow': {
    under(c: GatherCtx): void {
      const r = c.row;
      if (c.full > 0) {
        if (c.shaft) ringGlow(c, c.hx, c.hy, r.widthPx, c.cargo, KEEL_FULL_SHAFT_ALPHA * c.full);
        else glow(c, c.hx, c.hy, r.r2Px, c.cargo, KEEL_FULL_ALPHA * c.full);
        return;
      }
      if (!c.atRest || c.work <= 0) return;
      // The breath: 1 at the start of the lot, 0 at its end. The shaft beats
      // on the same figure, and its lot is four times as long in wall time.
      const b = 1 - ease(c.lot);
      if (c.shaft) {
        ringGlow(
          c, c.hx, c.hy, lerp(r.lengthPx, r.length2Px, b),
          lighten(c.res, KEEL_SHAFT_LIFT), c.work * lerp(r.alpha3, r.alpha4, b),
        );
        return;
      }
      glow(
        c, c.hx, c.hy, lerp(r.rPx, r.r2Px, b) + r.driftPx * c.step,
        c.res, c.work * lerp(r.alpha, r.alpha2, b),
      );
    },
  },
};

/**
 * The cards of this file that wait for their own recipe.
 *
 * All three are built, so the table is empty. `gather-styles.ts` reads it
 * back and `gather-recipes-seabed.test.ts` refuses an id that stands in
 * neither table.
 */
export const PENDING: readonly string[] = [];
